import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { setLoading } from 'state/loading'
import DataGridWithSearch from 'components/generic/datagrid/DataGridWithSearch'
import StyledLink from 'components/generic/typography/StyledLink.jsx'
import Tooltip from 'components/generic/tooltip'
import { formatNumber } from 'utils/helpers'
import { generateRenderHeaderFunction } from 'utils/generateRenderHeaderFunction'
import { SORTABLE_INVOICE_FIELDS } from 'utils/constants'

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0),
    },
    '& .MuiDataGrid-row': {
      cursor: 'pointer',
    },
  },
}))

const getColumns = (handleNavigation, renderHeaderWithSort, enableNegativeIbtToInvoice = false) => [
  {
    field: 'invoiceNumber',
    headerName: 'Invoice #',
    flex: 1,
    renderCell: params => (
      <StyledLink
        to={params?.row?.id}
        onClick={event => {
          event.preventDefault()
          event.stopPropagation()
          if (event.metaKey) {
            window.open(`invoices/${params?.row?.id}`, '_blank')
          } else {
            handleNavigation(params)
          }
        }}
      >
        {params.row.invoiceNumber}
      </StyledLink>
    ),
    renderHeader: () =>
      renderHeaderWithSort('Invoice #', SORTABLE_INVOICE_FIELDS.INVOICE_TICKET_ID, { startAscending: false }),
    sortable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    renderHeader: () => renderHeaderWithSort('Status', SORTABLE_INVOICE_FIELDS.STATUS),
    sortable: false,
  },
  {
    field: 'customer',
    headerName: 'Customer',
    flex: 2,
    renderHeader: () => renderHeaderWithSort('Customer', SORTABLE_INVOICE_FIELDS.CUSTOMER_DISPLAY_NAME),
    sortable: false,
  },
  {
    field: 'tickets',
    headerName: '# of Tickets',
    headerAlign: 'right',
    align: 'right',
    flex: 1,
    sortable: false,
  },
  {
    field: 'outboundTicketNumber',
    headerName: enableNegativeIbtToInvoice ? 'Ticket #' : 'OBT #',
    flex: 1,
    renderCell: params => {
      if (enableNegativeIbtToInvoice) {
        // Support both new 'tickets' structure and legacy 'outboundTicketTags'
        const { tickets = [], outboundTicketTags = [] } = params.row

        // Use new tickets structure if available, otherwise fall back to legacy structure
        const ticketTags = tickets.length > 0 ? tickets.map(ticket => ticket.tag) : outboundTicketTags

        if (ticketTags?.length > 1) {
          return (
            <Tooltip content={ticketTags.join(', ')}>
              <>Multiple</>
            </Tooltip>
          )
        }
        return ticketTags?.[0] || ''
      } else {
        // Legacy behavior - only show outbound ticket tags
        const { outboundTicketTags = [] } = params.row
        if (outboundTicketTags?.length > 1) {
          return (
            <Tooltip content={outboundTicketTags.join(', ')}>
              <>Multiple</>
            </Tooltip>
          )
        }
        return outboundTicketTags?.[0] || ''
      }
    },
    sortable: false,
  },
  {
    field: 'orderNumber',
    headerName: enableNegativeIbtToInvoice ? 'Order #' : 'SO #',
    flex: 1,
    renderCell: ({ row }) => {
      if (enableNegativeIbtToInvoice) {
        const { orders = [], salesOrderNumber } = row
        if (orders.length === 1) return orders[0].number || ''
        if (orders.length > 1) {
          const orderNumbers = orders.map(o => o.number).filter(Boolean)
          if (orderNumbers.length === 0) return ''
          return (
            <Tooltip content={orderNumbers.join(', ')}>
              <>Multiple</>
            </Tooltip>
          )
        }
        return salesOrderNumber || ''
      } else {
        // Legacy behavior - only show sales order number
        const { salesOrderNumber } = row
        return salesOrderNumber || ''
      }
    },
    renderHeader: () =>
      renderHeaderWithSort(
        enableNegativeIbtToInvoice ? 'Order #' : 'SO #',
        SORTABLE_INVOICE_FIELDS.SALES_ORDER_TICKET_ID,
        { startAscending: false },
      ),
    sortable: false,
  },
  {
    field: 'materialTypeCommonNames',
    headerName: 'Materials',
    flex: 2,
    renderCell: params => {
      const { materialTypeCommonNames = [] } = params.row
      if (materialTypeCommonNames?.length > 1) {
        return (
          <Tooltip content={materialTypeCommonNames.join(', ')}>
            <>Mixed</>
          </Tooltip>
        )
      }
      return materialTypeCommonNames?.[0] || ''
    },
    sortable: false,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    renderCell: params => `${params?.row?.pricePrepend}${formatNumber(params?.row?.amount)}`,
    renderHeader: () => renderHeaderWithSort('Amount', SORTABLE_INVOICE_FIELDS.TOTAL, { startAscending: false }),
    sortable: false,
  },
  {
    field: 'issueDate',
    headerName: 'Issue Date',
    type: 'date',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    renderHeader: () =>
      renderHeaderWithSort('Issue Date', SORTABLE_INVOICE_FIELDS.ISSUE_DATE, { startAscending: false }),
    sortable: false,
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    type: 'date',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    renderHeader: () => renderHeaderWithSort('Due Date', SORTABLE_INVOICE_FIELDS.DUE_DATE, { startAscending: false }),
    sortable: false,
  },
]

const InvoiceGrid = props => {
  const { renderSelectFilter, allowedOptions, filter, setFilter } = props
  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Feature flags
  const { supportVoidInvoice, enableNegativeIbtToInvoice } = useSelector(state => state.featureFlags?.variables || {})

  const renderHeaderWithSort = useMemo(() => generateRenderHeaderFunction(filter, setFilter), [filter])

  const handleNavigation = params => {
    dispatch(setLoading(true))
    setTimeout(() => {
      navigate(`/app/sales/invoices/${params.row.id}`)
    }, 0)
  }

  const tableColumns = getColumns(handleNavigation, renderHeaderWithSort, enableNegativeIbtToInvoice)

  return (
    <DataGridWithSearch
      {...props}
      columns={tableColumns}
      onRowClick={(params, event) => {
        if (event.metaKey) {
          window.open(`invoices/${params?.row?.id}`, '_blank')
        } else {
          handleNavigation(params)
        }
      }}
      className={classes.root}
      rowsPerPageOptions={[10, 20, 30, 50, 100]}
      renderSelectFilter={supportVoidInvoice ? renderSelectFilter : () => null}
      allowedOptions={supportVoidInvoice ? allowedOptions : []}
    />
  )
}

InvoiceGrid.propTypes = DataGridWithSearch.propTypes

InvoiceGrid.defaultProps = DataGridWithSearch.defaultProps

export default InvoiceGrid
